import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { AquasaveLogo } from "@/components/AquasaveLogo";
import NetworkMap from "@/components/NetworkMap";
import KpiCards from "@/components/KpiCards";
import AlertsPanel from "@/components/AlertsPanel";
import AiPanel from "@/components/AiPanel";
import TelemetryIngest from "@/components/TelemetryIngest";
import ArereReport from "@/components/ArereReport";
import Sparkline from "@/components/Sparkline";
import PeriodSelector from "@/components/PeriodSelector";
import ThresholdDialog from "@/components/ThresholdDialog";
import MultiMeterChart from "@/components/MultiMeterChart";
import ForecastPanel from "@/components/ForecastPanel";
import AlertsHistoryPanel from "@/components/AlertsHistoryPanel";
import TrattoLossChart from "@/components/TrattoLossChart";
import DistrictComparePanel from "@/components/DistrictComparePanel";
import { useAlertsWebSocket } from "@/hooks/useAlertsWebSocket";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import api, { API } from "@/lib/api";
import { SignOut, Printer, PaperPlaneTilt, ArrowsClockwise, Broadcast, MapTrifold, ListChecks, GearSix, DownloadSimple, WarningOctagon, FileXls, FilePdf } from "@phosphor-icons/react";

export default function DashboardPage() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [metrics, setMetrics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [showReport, setShowReport] = useState(false);
  const [period, setPeriod] = useState("live");
  const [sessionAlerts, setSessionAlerts] = useState([]);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/v1/metrics?period=${period}`);
      setMetrics(data);
    } catch (e) {
      toast.error("Errore caricamento metrics");
    } finally { setLoading(false); }
  }, [period]);

  useEffect(() => { load(); }, [load]);

  const sendAlertEmail = async () => {
    setSending(true);
    try {
      const { data } = await api.post("/v1/send-email-alert", {});
      toast.success(`Email allarme inviata a ${data.recipient} (${data.alerts_count} eventi)`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore invio email");
    } finally { setSending(false); }
  };

  const handlePrint = () => {
    setShowReport(true);
    setTimeout(() => window.print(), 300);
  };

  const handleLogout = () => { logout(); nav("/"); };

  // Real-time alert notifications
  const handleWsAlert = useCallback((msg) => {
    toast.error(msg.messaggio || "Nuova anomalia rilevata", {
      duration: 8000,
      icon: "🚨",
      description: `Meter ${msg.meter_id} · Z-Score ${msg.z_score?.toFixed?.(2)} (soglia ${msg.threshold})`,
    });
    setSessionAlerts((prev) => [{ ...msg, timestamp: msg.timestamp || new Date().toISOString() }, ...prev].slice(0, 200));
    load();
  }, [load]);
  const { connected: wsConnected } = useAlertsWebSocket({ onAlert: handleWsAlert, enabled: !!user });

  const downloadExport = async (format = "csv") => {
    try {
      const token = localStorage.getItem("aqua_token");
      const url = `${API}/v1/export/${format}?period=${period}`;
      const resp = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const blob = await resp.blob();
      const cd = resp.headers.get("content-disposition") || "";
      const match = /filename="?([^";]+)"?/i.exec(cd);
      const filename = match ? match[1] : `aquasave_${period}.${format}`;
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = filename;
      link.click();
      URL.revokeObjectURL(link.href);
      toast.success(`Export ${format.toUpperCase()} scaricato · ${filename}`);
    } catch (e) {
      toast.error(`Errore export: ${e.message}`);
    }
  };

  const downloadBenchmarkPdf = async () => {
    try {
      const token = localStorage.getItem("aqua_token");
      const url = `${API}/v1/export/pdf/benchmark`;
      const resp = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const blob = await resp.blob();
      const cd = resp.headers.get("content-disposition") || "";
      const match = /filename="?([^";]+)"?/i.exec(cd);
      const filename = match ? match[1] : `aquasave_benchmark.pdf`;
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = filename;
      link.click();
      URL.revokeObjectURL(link.href);
      toast.success(`Benchmark PDF · ${filename}`);
    } catch (e) {
      toast.error(`Errore benchmark: ${e.message}`);
    }
  };

  if (loading || !metrics) {
    return (
      <div className="min-h-screen grid place-items-center bg-[#070B14]">
        <div className="flex items-center gap-3 text-slate-400 font-mono text-sm">
          <ArrowsClockwise className="animate-spin" size={20} /> Sincronizzazione telemetria...
        </div>
      </div>
    );
  }

  const { arera, alerts, distretto, utenze } = metrics;

  return (
    <div className="min-h-screen bg-[#070B14] text-slate-100">
      {/* Top bar */}
      <header className="no-print sticky top-0 z-30 border-b border-slate-800 bg-[#070B14]/85 backdrop-blur-xl">
        <div className="mx-auto max-w-[1600px] px-6 py-3 flex items-center justify-between">
          <div className="flex items-center gap-6">
            <AquasaveLogo />
            <div className="hidden md:flex items-center gap-2 pl-6 border-l border-slate-800">
              <Broadcast size={14} className={wsConnected ? "text-[#10B981] animate-pulse" : "text-slate-600"} />
              <span className="text-xs font-mono uppercase tracking-widest text-slate-400">
                {wsConnected ? "Live" : "Offline"} · {distretto.nome}
              </span>
              {wsConnected && (
                <span className="ml-1 text-[9px] font-mono px-1.5 py-0.5 rounded bg-emerald-500/10 text-emerald-400 border border-emerald-500/30" data-testid="ws-status">WS</span>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2">
            {user?.role === "admin" && (
              <Button variant="outline" size="sm" onClick={() => nav("/admin")} data-testid="admin-button" className="border-slate-700 hover:bg-slate-800 gap-2">
                <GearSix size={14} /> Admin
              </Button>
            )}
            <TelemetryIngest utenze={utenze} onIngested={load} />
            <Button variant="outline" size="sm" onClick={() => downloadExport("csv")} data-testid="export-csv-button" className="border-slate-700 hover:bg-slate-800 gap-2">
              <DownloadSimple size={14} /> CSV
            </Button>
            <Button variant="outline" size="sm" onClick={() => downloadExport("xlsx")} data-testid="export-xlsx-button" className="border-slate-700 hover:bg-slate-800 gap-2">
              <FileXls size={14} /> XLSX
            </Button>
            <Button variant="outline" size="sm" onClick={() => downloadExport("pdf")} data-testid="export-pdf-button" className="border-slate-700 hover:bg-slate-800 gap-2">
              <FilePdf size={14} /> PDF
            </Button>
            <Button variant="outline" size="sm" onClick={downloadBenchmarkPdf} data-testid="export-benchmark-button" className="border-slate-700 hover:bg-slate-800 gap-2">
              <FilePdf size={14} /> Benchmark
            </Button>
            <Button variant="outline" size="sm" onClick={load} data-testid="refresh-button" className="border-slate-700 hover:bg-slate-800 gap-2">
              <ArrowsClockwise size={14} /> Sync
            </Button>
            <Button variant="outline" size="sm" onClick={handlePrint} data-testid="print-report-button" className="border-slate-700 hover:bg-slate-800 gap-2">
              <Printer size={14} /> Report ARERA
            </Button>
            <Button size="sm" onClick={sendAlertEmail} disabled={sending || alerts.length===0} data-testid="send-email-button"
              className="bg-[#EF4444] hover:bg-red-600 text-white gap-2 disabled:opacity-50">
              <PaperPlaneTilt size={14} /> {sending ? "Invio..." : "Invia Allarme"}
            </Button>
            <div className="pl-3 ml-2 border-l border-slate-800 flex items-center gap-3">
              <div className="text-right hidden md:block">
                <div className="text-xs font-mono text-slate-300">{user?.email}</div>
                <div className="text-[10px] font-mono uppercase tracking-widest text-[#3EB6F0]">{user?.role}</div>
              </div>
              <Button variant="ghost" size="icon" onClick={handleLogout} data-testid="logout-button" className="hover:bg-slate-800">
                <SignOut size={16} />
              </Button>
            </div>
          </div>
        </div>
      </header>

      {showReport ? (
        <>
          <div className="no-print mx-auto max-w-[1200px] px-6 py-4 flex justify-between items-center">
            <div className="text-sm text-slate-400 font-mono">Anteprima Report ARERA · Ctrl+P per esportare in PDF</div>
            <Button variant="outline" size="sm" onClick={() => setShowReport(false)} data-testid="close-print-preview" className="border-slate-700">Chiudi anteprima</Button>
          </div>
          <div className="mx-auto max-w-[1200px] mb-10">
            <ArereReport metrics={metrics} />
          </div>
        </>
      ) : (
        <main className="mx-auto max-w-[1600px] px-6 py-6 space-y-6 no-print">
          {/* Headline */}
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
            <div>
              <div className="flex items-center gap-2 text-xs font-mono uppercase tracking-widest text-slate-500">
                <MapTrifold size={14} className="text-[#3EB6F0]" />
                <span>Control Room · Bilancio Idrico Real-Time</span>
              </div>
              <h1 className="font-heading text-3xl md:text-4xl font-bold tracking-tight text-white mt-1">
                {distretto.nome}
              </h1>
              <div className="text-sm text-slate-400 mt-1 font-mono">{distretto.gestore} · Rete {distretto.lunghezza_rete_km} km</div>
            </div>
            <Badge variant="outline" className="border-slate-700 bg-slate-900/50 text-slate-300 font-mono text-xs">
              Ultimo update · {new Date(metrics.timestamp).toLocaleTimeString("it-IT")}
            </Badge>
          </div>

          <div className="flex items-center justify-between gap-4 flex-wrap">
            <PeriodSelector value={period} onChange={setPeriod} />
            <div className="text-xs font-mono text-slate-500">
              {period === "live" ? "Snapshot in tempo reale" : `Media aggregata ultimi ${period === "7d" ? "7 giorni" : period === "30d" ? "30 giorni" : "365 giorni"}`}
            </div>
          </div>

          <KpiCards arera={arera} alertsCount={alerts.length} />

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
            <div className="lg:col-span-8 min-h-[520px] bg-[#111827] border border-slate-800 rounded-lg overflow-hidden">
              <NetworkMap metrics={metrics} />
            </div>
            <div className="lg:col-span-4">
              <AlertsPanel alerts={alerts} />
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
            <div className="lg:col-span-7">
              <MultiMeterChart utenze={utenze} />
            </div>
            <div className="lg:col-span-5">
              <AlertsHistoryPanel sessionAlerts={sessionAlerts} onClear={() => setSessionAlerts([])} />
            </div>
          </div>

          <TrattoLossChart tratti={metrics.tratti} />

          <DistrictComparePanel />

          <ForecastPanel />

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
            <div className="lg:col-span-7">
              <AiPanel utenze={utenze} />
            </div>
            <div className="lg:col-span-5 bg-[#111827] border border-slate-800 rounded-lg" data-testid="utenze-table">
              <div className="px-5 py-4 border-b border-slate-800 flex items-center gap-2">
                <ListChecks size={18} className="text-[#3EB6F0]" />
                <div>
                  <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold">Allacci</div>
                  <div className="font-heading text-lg text-white">Utenze Monitorate</div>
                </div>
              </div>
              <div className="overflow-y-auto max-h-[420px]">
                <table className="w-full text-xs">
                  <thead className="text-slate-500 uppercase tracking-widest">
                    <tr className="border-b border-slate-800">
                      <th className="text-left px-4 py-2 font-mono">ID</th>
                      <th className="text-left px-4 py-2 font-mono">Indirizzo</th>
                      <th className="text-center px-2 py-2 font-mono">24h</th>
                      <th className="text-right px-2 py-2 font-mono">L/h</th>
                      <th className="text-right px-2 py-2 font-mono">Z</th>
                      <th className="text-center px-3 py-2 font-mono">Azioni</th>
                    </tr>
                  </thead>
                  <tbody className="font-mono">
                    {utenze.map((u) => (
                      <tr key={u.id} data-testid={`utenza-row-${u.id}`} className="border-b border-slate-800/60 hover:bg-slate-800/30">
                        <td className="px-4 py-2 text-slate-300">{u.id}</td>
                        <td className="px-4 py-2 text-slate-400 text-[11px]">{u.indirizzo}</td>
                        <td className="px-2 py-2">
                          <div className="flex justify-center">
                            <Sparkline data={u.history_24h} color={u.allarme === "HIGH" ? "#EF4444" : "#3EB6F0"} testId={`sparkline-${u.id}`} />
                          </div>
                        </td>
                        <td className="px-2 py-2 text-right text-slate-200">{(u.flusso_periodo_lh ?? u.flusso_istantaneo_lh).toFixed(0)}</td>
                        <td className="px-2 py-2 text-right text-slate-300">
                          {u.z_score}
                          <span className="text-[9px] text-slate-500 ml-1">/{u.z_threshold?.toFixed?.(1) ?? "2.5"}</span>
                        </td>
                        <td className="px-3 py-2">
                          <div className="flex items-center justify-center gap-2">
                            <span className={`inline-block w-2 h-2 rounded-full ${u.allarme === "HIGH" ? "bg-[#EF4444] marker-pulse" : "bg-[#10B981]"}`}></span>
                            <ThresholdDialog meterId={u.id} current={u.z_threshold ?? 2.5} onSaved={load} disabled={user?.role !== "admin"} />
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </main>
      )}
    </div>
  );
}
